import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { getCurrentPage, selectPreviousPage } from '../../selectors/routing';
import { selectAvailablePages } from '../../selectors/progress';
import { selectPurchasedPolicies } from '../../selectors';
import { PAGES, pageOrder } from '../../constants';
const steppablePageOrder = pageOrder.filter((page: string) => page !== PAGES.SUMMARY && page !== PAGES.PAYMENT);

const MobileHeader = () => {
  const currentPage = useSelector(getCurrentPage);
  const availablePages: string[] = useSelector(selectAvailablePages);
  const previousPage = useSelector(selectPreviousPage);
  const policyBound = useSelector(selectPurchasedPolicies).length > 0;
  const dispatch = useDispatch();
  const isIndustryPage = currentPage === PAGES.INDUSTRY;
  const isSummaryPage = currentPage === PAGES.SUMMARY;
  const isPaymentPage = currentPage === PAGES.PAYMENT;
  const isErrorPage = currentPage === PAGES.ERROR;
  const isQuoteHelp = currentPage === PAGES.QUOTEHELP;

  // hide the entire mobile nav once a policy is bound — the PAYMENT page becomes the
  // confirmation screen and there's no valid back navigation from there
  if (isErrorPage || isQuoteHelp || policyBound) return null;

  const showBackButton = !isIndustryPage && !isSummaryPage;
  const showStepper = !isSummaryPage && !isPaymentPage;

  const steppablePages = availablePages.filter((page: string) => page !== PAGES.SUMMARY && page !== PAGES.PAYMENT);
  const currentStepIndex = steppablePages.indexOf(currentPage);
  const currentStep = currentStepIndex >= 0 ? currentStepIndex + 1 : 1;
  const totalSteps = steppablePageOrder.length;
  const progressPercent = totalSteps > 0 ? (currentStep / totalSteps) * 100 : 0;

  return (
    <HeaderContainer isSummaryPage={isSummaryPage}>
      {showBackButton && (
        <BackContainer>
          <BackButton
            onClick={() => previousPage && dispatch({ type: previousPage })}
            data-cy="mobile-back-button"
            aria-label="Go back"
          >
            <ArrowIcon aria-hidden="true">&larr;</ArrowIcon>
            Back
          </BackButton>
        </BackContainer>
      )}
      {showStepper && (
        <StepperContainer>
          <StepLabel>Step {currentStep}</StepLabel>
          <ProgressBarTrack>
            <ProgressBarFill
              style={{ width: `${progressPercent}%` }}
              role="progressbar"
              aria-valuenow={currentStep}
              aria-valuemin={1}
              aria-valuemax={totalSteps}
              aria-label={`Step ${currentStep} of ${totalSteps}`}
            />
            <ProgressIndicator style={{ left: `${progressPercent}%` }} />
          </ProgressBarTrack>
        </StepperContainer>
      )}
    </HeaderContainer>
  );
};

const HeaderContainer = styled.div<{ isSummaryPage: boolean }>`
  font-family: ${({ theme }) => theme.font.typeface.primary};
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: ${({ theme }) => theme.colors.neutral.white};
`;

const BackContainer = styled.div`
  padding: 16px 24px;
  border-bottom: 1px solid #d4d4dd;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  outline: none;
  cursor: pointer;
  padding: 0;
  font-family: ${({ theme }) => theme.font.typeface.primary};
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary.zero};
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ArrowIcon = styled.span`
  font-size: 16px;
`;

const StepperContainer = styled.div`
  padding: 24px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StepLabel = styled.span`
  font-family: ${({ theme }) => theme.font.typeface.primary};
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.tertiary.sixty};
  letter-spacing: 1px;
  text-transform: uppercase;
  white-space: nowrap;
`;

const ProgressBarTrack = styled.div`
  position: relative;
  flex: 1;
  height: 4px;
  background-color: ${({ theme }) => theme.colors.neutral.special};
  border-radius: 100px;
`;

const ProgressBarFill = styled.div`
  height: 100%;
  background-color: ${({ theme }) => theme.colors.primary.zero};
  border-radius: 100px;
  transition: width 0.3s ease;
`;

const ProgressIndicator = styled.div`
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 8px;
  height: 8px;
  background-color: ${({ theme }) => theme.colors.secondary.zero};
  border-radius: 50%;
  box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary.zero};
`;

export default MobileHeader;
